import React, { useState, useEffect } from 'react'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid2'
import Stack from '@mui/material/Stack'
import defaultTexts from '../../../texts/texts'
import IconButton from '@mui/material/IconButton'
import { FaFileArrowUp } from 'react-icons/fa6'
import { capitalizeFirstLetter } from '../../_common/_helpers/string'
import { getTextedDate, getFormattedDateTime } from '../_helpers/dates'
import constants from '../../../config/constants'
import parse from 'html-react-parser'
import CustomDialog from '../../_common/CustomDialog'
import CertificateView from './CertificateView'

const CustomerUiView = (props) => {
  const { config_params, customerUiCertificateJobs } = props
  const theme = useTheme()
  
  const [certificates, setCertificates] = useState([])

  const [documentDialogOpen, setDocumentDialogOpen] = useState(false)
  const [documentTitle, setDocumentTitle] = useState('')
  const [documentText, setDocumentText] = useState('')

  const keySmStyle = {
    color: theme.palette.vodka,
    fontSize: '16px',
    fontWeight: '500',
    marginBottom: '10px',
  }

  const keyLgStyle = {
    color: theme.palette.grayX11,
    fontSize: '18px',
    fontWeight: '500',
    marginBottom: '10px',
  }

  const valueSmStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '16px',
    fontWeight: '500',
    marginBottom: '20px',
  }

  const valueMdStyle = {
    color: theme.palette.greenSheen,
    fontSize: '16px',
    fontWeight: '500',
  }

  const noLogoStyle = {
    width: '200px',
    height: '80px',
    borderRadius: '10px',
    border: `${theme.palette.lightSilver} 1px dashed`,
    color: theme.palette.lightSilver,
    fontSize: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    textTransform: 'uppercase',
  }

  const pickColorStyle = {
    width: '60px',
    height: '60px',
    borderRadius: '30px',
  }

  const regFieldStyle = {
    fontSize: '16px',
    fontWeight: '400',
    marginBottom: '10px',
  }

  const colorPickerIconSmallStyle = {
    width: '24px',
    height: '24px',
    borderRadius: '12px',
  }

  const handleDocumentDialogOpen = (title, text) => {
    setDocumentDialogOpen(true)
    setDocumentTitle(title)
    setDocumentText(text)
  }

  const handleDocumentDialogClose = () => {
    setDocumentDialogOpen(false)
    setDocumentTitle('')
    setDocumentText('')
  }

  const handleClickDocument = (document) => {
    if (document.type === 'string') {
      handleDocumentDialogOpen(document.label, document.value)
    } else if (document.type === 'file') {
      window.open(constants.s3URL + constants.s3Bucket + document.value, '_blank')
    } else if (document.type === 'url') {
      window.open(document.value, '_blank')
    }
  }

  useEffect(() => {
    const getDomainsByJobId = (job_id) => {
      let result = []
      const filtered_jobs = customerUiCertificateJobs.filter(item => item.job_id === job_id)

      filtered_jobs.forEach(item => {
        result.push(item.domain)
      })

      return result
    }

    let _certificates = []
    let job_ids = []

    customerUiCertificateJobs.forEach(c => {
      const domains = getDomainsByJobId(c.job_id)
      const expired = c.expiration_date && getFormattedDateTime(c.expiration_date) <= getFormattedDateTime()

      if (!job_ids.includes(c.job_id)) {
        _certificates.push({
          domains,
          is_valid: c.cert_type === 'generate' || !expired,
          type: c.cert_type,
          expiration_date: c.expiration_date ? getTextedDate(c.expiration_date) : '',
          expired,
        })

        job_ids.push(c.job_id)
      }      
    })

    setCertificates(_certificates)
  }, [customerUiCertificateJobs])

  return (
    <>
      {
        (config_params && Object.keys(config_params).length !== 0) ?
        <Grid container spacing={4}>

          <Grid
            size={{ xs: 12, lg: 6 }}
            sx={{ borderRight: `${theme.palette.lightSilver} 1px dotted` }}
          >

            <Box sx={keySmStyle}>{defaultTexts.userInterfaceDomain}:</Box>
            <Box sx={{ marginBottom: '20px' }}>
              {
                certificates.map((item, index) => (
                  <CertificateView key={index} {...item} />
                ))
              }
            </Box>

            <Box sx={keySmStyle}>{defaultTexts.googleRecaptchaConfiguration}:</Box>
            <Box sx={keyLgStyle}>{defaultTexts.siteKey}:</Box>
            <Box sx={valueSmStyle}>{ config_params.captcha_config ? config_params.captcha_config.site_key : '' }</Box>

            <Box sx={keyLgStyle}>{defaultTexts.secretKey}:</Box>
            <Box sx={valueSmStyle}>{ config_params.captcha_config ? config_params.captcha_config.secret_key : '' }</Box>

            <Box sx={keySmStyle}>{defaultTexts.defaultOpenStackDomain}:</Box>
            <Box sx={valueSmStyle}>{config_params.openstack_domain}</Box>

            <Box sx={keySmStyle}>{defaultTexts.defaultOpenStackUserRole}:</Box>
            <Box sx={valueSmStyle}>{config_params.openstack_role}</Box>            

            <Box sx={keySmStyle}>{defaultTexts.registrationFormFields}:</Box>
            <Box sx={valueSmStyle}>{ config_params.signup_config.enabled ? defaultTexts.signUpEnabled : defaultTexts.signUpDisabled }</Box>
            {
              config_params.signup_config && config_params.signup_config.form_fields.map((item, index) => (
                <Box key={index}>
                  {
                    (item.field_key !== 'tou' && item.field_key !== 'pp') &&
                    <Box  sx={regFieldStyle}>
                      <Grid container spacing={4}>
                        <Grid size={{ xs: 7, xl: 6 }}>
                          <Box sx={{ color: theme.palette.oxfordBlue }}>{item.label}</Box>
                        </Grid>
                        <Grid size={{ xs: 5, xl: 6 }}>
                          { item.required && <Box sx={{ color: theme.palette.coralRed }}>{defaultTexts.required}</Box>  }
                        </Grid>
                      </Grid>
                    </Box>
                  }
                </Box>                
              ))
            }

            {
              config_params.signup_config && config_params.signup_config.documents.map((item, index) => (
                <Box key={index} sx={regFieldStyle}>
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='flex-start'
                    spacing={2}
                  >
                    <Box sx={{ color: theme.palette.oxfordBlue }}>{item.label}</Box>
                    <IconButton onClick={() => handleClickDocument(item)}>
                      <FaFileArrowUp size={40} color={theme.palette.davysGrey} />
                    </IconButton>
                  </Stack>
                </Box>
              ))
            }

          </Grid>

          {/* --------------------------------------------------------------------------------- */}
          {/* --------------------------------------------------------------------------------- */}
          {/* --------------------------------------------------------------------------------- */}
          {/* --------------------------------------------------------------------------------- */}
          {/* --------------------------------------------------------------------------------- */}

          <Grid size={{ xs: 12, lg: 6 }}>
            
            <Box sx={keySmStyle}>{defaultTexts.customizeYourInterfaceAppearance}:</Box>

            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
              spacing={2}
              sx={{
                borderBottom: `${theme.palette.lightSilver} 1px dotted`,
                paddingBottom: '10px',
                marginBottom: '20px',
              }}
            >
              <Box>
                {
                  config_params.logo ?
                  <img src={constants.s3URL + constants.s3Bucket + config_params.logo} alt='' style={{ maxWidth: '200px' }} /> :
                  <Box sx={noLogoStyle}>{defaultTexts.noLogo}</Box>
                }
              </Box>
              <Box sx={valueMdStyle}>{defaultTexts.customerUiLogo}</Box>
            </Stack>

            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
              spacing={2}
              sx={{ marginBottom: '30px' }}
            >
              <Box sx={{ ...pickColorStyle, background: config_params.primary_color }}></Box>
              <Box sx={valueMdStyle}>{defaultTexts.primaryColor}</Box>
            </Stack>

            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
              spacing={2}
              sx={{ marginBottom: '30px' }}
            >
              <Box sx={{ ...pickColorStyle, background: config_params.secondary_color }}></Box>
              <Box sx={valueMdStyle}>{defaultTexts.secondaryColor}</Box>
            </Stack>

            <Box sx={keySmStyle}>{defaultTexts.backgroundType}:</Box>
            <Box sx={valueSmStyle}>{ config_params.promo_config ? capitalizeFirstLetter(config_params.promo_config.background_type) : '' }</Box>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
              spacing={2}
              sx={{ marginBottom: '20px' }}
            >
              <Box>
                {
                  config_params.promo_config && config_params.promo_config.background_type === 'color' ?
                  <>
                    {
                      config_params.promo_config.background_color_type === 'solid' ?
                      <Box sx={{ ...colorPickerIconSmallStyle, backgroundColor: config_params.promo_config.background_color }}></Box> :
                      <>
                        <Box sx={{ ...colorPickerIconSmallStyle, backgroundColor: config_params.promo_config.gradient_color_1, marginBottom: '5px' }}></Box>
                        <Box sx={{ ...colorPickerIconSmallStyle, backgroundColor: config_params.promo_config.gradient_color_2 }}></Box>
                      </>
                    }
                  </> :
                  <>
                    {
                      config_params.promo_config &&
                      <img src={constants.s3URL + constants.s3Bucket + config_params.promo_config.background_image} alt='' style={{ maxWidth: '200px' }} />
                    }
                  </>                  
                }
              </Box>
              <Box sx={valueSmStyle}>
                {
                  (config_params.promo_config && config_params.promo_config.background_type === 'color') ?
                  <>
                    {
                      config_params.promo_config.background_color_type === 'solid' ?
                      <>{defaultTexts.backgroundColor}</> :
                      <>
                        <Box sx={{ marginBottom: '5px' }}>{defaultTexts.gradientColor} 1</Box>
                        <Box>{defaultTexts.gradientColor} 2</Box>
                      </>
                    }
                  </> :
                  <>{defaultTexts.backgroundImage}</>
                }
              </Box>              
            </Stack>

            {
              (config_params.promo_config && config_params.promo_config.heading) &&
              <>
                <Box sx={keySmStyle}>{defaultTexts.headingText}:</Box>
                <Box sx={valueSmStyle}>{config_params.promo_config.heading}</Box>
              </>
            }

            {
              (config_params.promo_config && config_params.promo_config.promo_text && config_params.promo_config.promo_text.length > 0) && config_params.promo_config.promo_text.map((item, index) => (
                <Box key={index}>
                  <Box sx={keySmStyle}>{defaultTexts.promoText} {index + 1}:</Box>
                  <Box sx={valueSmStyle}>{parse(item)}</Box>
                </Box>
              ))
            }

            {
              (config_params.contact_us && config_params.contact_us.company) &&
              <>
                <Box sx={keySmStyle}>{defaultTexts.company}:</Box>
                <Box sx={valueSmStyle}>{config_params.contact_us.company}</Box>
              </>
            }

            {
              (config_params.contact_us && config_params.contact_us.phone && config_params.contact_us.phone.length > 0) && config_params.contact_us.phone.map((item, index) => (
                <Box key={index}>
                  <Box sx={keySmStyle}>{defaultTexts.phone} {index + 1}:</Box>
                  <Box sx={valueSmStyle}>{item}</Box>
                </Box>
              ))
            }

            {
              (config_params.contact_us && config_params.contact_us.email && config_params.contact_us.email.length > 0) && config_params.contact_us.email.map((item, index) => (
                <Box key={index}>
                  <Box sx={keySmStyle}>{defaultTexts.email} {index + 1}:</Box>
                  <Box sx={valueSmStyle}>{item}</Box>
                </Box>
              ))
            }

          </Grid>

        </Grid> :
        <Box
          sx={{
            padding: '20px',
            fontSize: '20px',
          }}
        >
          {defaultTexts.notConfigured}
        </Box>
      }

      <CustomDialog
        open={documentDialogOpen}
        onClose={handleDocumentDialogClose}
        dialogTitle={{ title: documentTitle }}
        dialogBody={{ text: documentText }}
      />
    </>
  )
}

export default CustomerUiView